import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { DamageTypes, DamageTypesName } from '../../constants/DamageTypes';
import deleteIcon from '../../assets/img/delete.svg';
import editIcon from '../../assets/img/edit.svg';

function ConditionInspectionList({
  damages,
  onEdit,
  onDelete,
  title = 'Condition Inspection – list',
  emptyMessage = 'No damage items found',
  showEmptyState = false,
  className = '',
  itemClassName = '',
  isLoading = false,
}) {
  const getDamageTypeLabel = useCallback((damage) => {
    const exactMatch = DamageTypes.find(
      (type) =>
        type.value === damage.damageType ||
        type.value === damage.type ||
        type.value === damage.damageType?.value,
    );
    if (exactMatch) return exactMatch.label;

    const typeValue = damage.damageType?.value || damage.damageType || damage.type;
    if (typeValue && DamageTypesName[typeValue.toUpperCase()]) {
      return DamageTypesName[typeValue.toUpperCase()];
    }

    return damage.label || damage.damageType?.label || 'Unknown';
  }, []);
  const handleEditClick = useCallback(
    (damageId) => (event) => {
      event.preventDefault();
      onEdit(damageId);
    },
    [onEdit],
  );

  const handleDeleteClick = useCallback(
    (damageId) => (event) => {
      event.preventDefault();
      onDelete(damageId);
    },
    [onDelete],
  );

  const handleKeyDown = useCallback(
    (damageId, action) => (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (action === 'edit') {
          onEdit(damageId);
        } else if (action === 'delete') {
          onDelete(damageId);
        }
      }
    },
    [onEdit, onDelete],
  );

  const listContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="text-center py-3" role="status" aria-live="polite">
          <span className="visually-hidden">Loading damage items...</span>
          Loading...
        </div>
      );
    }

    if (!damages || damages.length === 0) {
      if (showEmptyState) {
        return (
          <div className="text-center py-3 text-muted" role="status">
            {emptyMessage}
          </div>
        );
      }
      return null;
    }
    return (
      <ul className="list-unstyled" aria-label={`${damages.length} damage items`}>
        {damages.map((damage, index) => {
          const damageTypeDisplay = getDamageTypeLabel(damage);
          const itemId = `damage-item-${damage.id}`;

          return (
            <li
              key={damage.id}
              className={`damage-list-item ${itemClassName}`.trim()}
              aria-labelledby={`${itemId}-info`}
            >
              <div
                className="damage-item-info"
                id={`${itemId}-info`}
                aria-describedby={`${itemId}-actions`}
              >
                <span className="damage-number" aria-label={`Item ${index + 1}`}>
                  {index + 1}.
                </span>
                <span className="damage-type" aria-label={`Damage type: ${damageTypeDisplay}`}>
                  {damageTypeDisplay}
                </span>
                <span
                  className="damage-description"
                  aria-label={`Description: ${damage.description || 'No description'}`}
                >
                  {damage.description}
                </span>
              </div>
              <div
                className="damage-item-actions"
                id={`${itemId}-actions`}
                role="group"
                aria-label={`Actions for ${damageTypeDisplay} damage`}
              >
                <Button
                  className="btn-icon me-2"
                  color="success"
                  size="sm"
                  onClick={handleEditClick(damage.id)}
                  onKeyDown={handleKeyDown(damage.id, 'edit')}
                  aria-label={`Edit ${damageTypeDisplay} damage item ${index + 1}`}
                  title={`Edit ${damageTypeDisplay} damage`}
                  type="button"
                >
                  <img src={editIcon} alt="edit" />
                  <span className="visually-hidden">Edit</span>
                </Button>
                <Button
                  className="btn-icon"
                  color="danger"
                  size="sm"
                  onClick={handleDeleteClick(damage.id)}
                  onKeyDown={handleKeyDown(damage.id, 'delete')}
                  aria-label={`Delete ${damageTypeDisplay} damage item ${index + 1}`}
                  title={`Delete ${damageTypeDisplay} damage`}
                  type="button"
                >
                  <img src={deleteIcon} alt="delete" />
                  <span className="visually-hidden">Delete</span>
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }, [
    damages,
    isLoading,
    showEmptyState,
    emptyMessage,
    itemClassName,
    handleEditClick,
    handleDeleteClick,
    handleKeyDown,
    getDamageTypeLabel,
  ]);

  if (!isLoading && (!damages || damages.length === 0) && !showEmptyState) {
    return null;
  }

  return (
    <section
      className={`condition-inspection-list mt-4 ${className}`.trim()}
      aria-labelledby="condition-list-title"
    >
      <h5 id="condition-list-title" className="mb-3">
        {title}
      </h5>
      {listContent}
    </section>
  );
}

ConditionInspectionList.propTypes = {
  damages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      damageType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        }),
      ]),
      description: PropTypes.string,
    }),
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  title: PropTypes.string,
  emptyMessage: PropTypes.string,
  showEmptyState: PropTypes.bool,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
  isLoading: PropTypes.bool,
};

ConditionInspectionList.defaultProps = {
  title: 'Condition Inspection – list',
  emptyMessage: 'No damage items found',
  showEmptyState: false,
  className: '',
  itemClassName: '',
  isLoading: false,
};

export default ConditionInspectionList;
